import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Collapse, Input, Popover, Skeleton, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { CollapseProps } from 'antd';
import { EyeOutlined, LinkOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { LeadOpsMatch } from '../../../api/leadOps';
import { normalizeTitle } from '../../../utils/format';
import { useUnsoldInventory } from '../../../hooks/useLeadOps';
import { AD_FORMAT_REFERENCE, normalizeAdFormat, parseAdFormats } from '../utils/adFormats';
import { MatchPreview, getInventoryStatus, inventoryStatusColor } from './CrmPanel';

const { Text } = Typography;

type UnsoldInventoryPanelProps = {
  onPitch: (match: LeadOpsMatch) => void;
};

type SortState = {
  field: string;
  order: 'ascend' | 'descend';
};

const SORT_DIRECTIONS: Array<'ascend' | 'descend'> = ['ascend', 'descend', 'ascend'];
const TABLE_SCROLL = { x: 1060, y: 420 } as const;

const getColumnText = (columns: LeadOpsMatch['columns_preview'] = [], title: string): string => {
  const wanted = normalizeTitle(title);
  const found = columns?.find((col) => normalizeTitle(col?.title || '') === wanted);
  return (found?.text || '').trim();
};

const parseNumeric = (value: string): number | null => {
  const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  if (!cleaned) return null;
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : null;
};

const compareNumeric = (a: number | null, b: number | null): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

const formatRate = (raw: string): string => {
  if (!raw) return '—';
  const num = parseNumeric(raw);
  if (num === null) return raw;
  return num.toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const formatExpectedDaily = (raw: string): string => {
  if (!raw) return '—';
  const num = parseNumeric(raw);
  if (num === null) return raw;
  return Math.round(num).toLocaleString();
};

const daysLeftTag = (days: number | null | undefined) => {
  if (typeof days !== 'number' || !Number.isFinite(days)) return null;
  if (days < 0) return <Tag color="red">Overdue</Tag>;
  const color = days <= 3 ? 'red' : days <= 14 ? 'orange' : 'default';
  return <Tag color={color}>{days === 0 ? 'Ends today' : `${days}d left`}</Tag>;
};

const matchFormats = (match: LeadOpsMatch): string[] =>
  parseAdFormats(getColumnText(match.columns_preview, 'Ad Format'));

const matchesTextFilter = (match: LeadOpsMatch, filter: string): boolean => {
  if (!filter) return true;
  const haystack = `${match.domain || ''} ${match.name || ''} ${match.domain_canonical || ''}`.toLowerCase();
  return haystack.includes(filter);
};

const matchesAdFormatFilter = (match: LeadOpsMatch, selectedNormalized: Set<string>): boolean => {
  if (selectedNormalized.size === 0) return true;
  return matchFormats(match).some((format) => selectedNormalized.has(normalizeAdFormat(format)));
};

const availableFormatLabels = (matches: LeadOpsMatch[]): string[] => {
  const present = new Set<string>();
  for (const match of matches) {
    for (const format of matchFormats(match)) {
      present.add(normalizeAdFormat(format));
    }
  }
  return AD_FORMAT_REFERENCE.filter((label) => present.has(normalizeAdFormat(label)));
};

const sectionLabel = (title: string, count: number) => (
  <div className="leadops-unsold-section-header">
    <Text strong>{title}</Text>
    <span className="leadops-match-group-count">{count}</span>
  </div>
);

export const UnsoldInventoryPanel: React.FC<UnsoldInventoryPanelProps> = ({ onPitch }) => {
  const [textFilter, setTextFilter] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [collapseKeys, setCollapseKeys] = useState<string[]>(['critical']);
  const [criticalSort, setCriticalSort] = useState<SortState | null>(null);
  const [readySort, setReadySort] = useState<SortState | null>(null);
  const query = useUnsoldInventory(true);
  const critical = useMemo(() => query.data?.data?.critical || [], [query.data?.data?.critical]);
  const ready = useMemo(() => query.data?.data?.ready || [], [query.data?.data?.ready]);
  const allMatches = useMemo(() => [...critical, ...ready], [critical, ready]);
  const formatChips = useMemo(() => availableFormatLabels(allMatches), [allMatches]);
  const selectedNormalized = useMemo(
    () => new Set(selectedFormats.map(normalizeAdFormat)),
    [selectedFormats],
  );
  const normalizedText = textFilter.trim().toLowerCase();
  const hasActiveFilters = Boolean(textFilter.trim()) || selectedFormats.length > 0;

  const filteredCritical = useMemo(() => {
    return critical.filter(
      (match) => matchesTextFilter(match, normalizedText) && matchesAdFormatFilter(match, selectedNormalized),
    );
  }, [critical, normalizedText, selectedNormalized]);
  const filteredReady = useMemo(() => {
    return ready.filter(
      (match) => matchesTextFilter(match, normalizedText) && matchesAdFormatFilter(match, selectedNormalized),
    );
  }, [ready, normalizedText, selectedNormalized]);

  const toggleFormat = (label: string) => {
    setSelectedFormats((current) => {
      const normalized = normalizeAdFormat(label);
      const exists = current.some((item) => normalizeAdFormat(item) === normalized);
      if (exists) return current.filter((item) => normalizeAdFormat(item) !== normalized);
      return [...current, label];
    });
  };

  const clearFilters = () => {
    setTextFilter('');
    setSelectedFormats([]);
  };

  const handleSortChange = (
    sorter: Parameters<NonNullable<TableProps<LeadOpsMatch>['onChange']>>[2],
    setSort: React.Dispatch<React.SetStateAction<SortState | null>>,
  ) => {
    const next = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = String(next?.columnKey || next?.field || '');
    if (!field) return;
    // Always derive next order ourselves so A↔Z never dead-ends on Ant Design's cancel step.
    setSort((prev) => {
      if (!prev || prev.field !== field) return { field, order: 'ascend' };
      return { field, order: prev.order === 'ascend' ? 'descend' : 'ascend' };
    });
  };

  const isLoading = query.isLoading || (query.isFetching && !query.data);
  const hasError = query.isError;
  const isEmpty = !isLoading && !hasError && critical.length === 0 && ready.length === 0;
  const filterEmpty = !isLoading && !hasError && !isEmpty
    && filteredCritical.length === 0 && filteredReady.length === 0;

  const buildColumns = useCallback((sort: SortState | null): ColumnsType<LeadOpsMatch> => [
    {
      title: 'Spot',
      key: 'spot',
      dataIndex: 'name',
      ellipsis: true,
      sorter: (a, b) => (a.name || a.domain || '').localeCompare(b.name || b.domain || ''),
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sort?.field === 'spot' ? sort.order : null,
      showSorterTooltip: false,
      render: (_value, match) => (
        <Text className="leadops-unsold-spot-name">{match.name || match.domain || '—'}</Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 260,
      render: (_value, match) => {
        const status = getInventoryStatus(match);
        const isCritical = status.trim().toLowerCase() === 'critical to sell';
        return (
          <Space size={[4, 4]}>
            {status && <Tag color={inventoryStatusColor(status)} style={{ margin: 0 }}>{status}</Tag>}
            {isCritical && daysLeftTag(match.days_to_end_date)}
          </Space>
        );
      },
    },
    {
      title: 'Ad Format',
      key: 'adFormat',
      width: 140,
      render: (_value, match) => {
        const formats = matchFormats(match);
        if (formats.length === 0) return '—';
        return (
          <Space size={[4, 4]} wrap>
            {formats.map((format) => (
              <Tag key={format} style={{ margin: 0 }}>{format}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Deal type',
      key: 'dealType',
      width: 100,
      render: (_value, match) => getColumnText(match.columns_preview, "Publisher's deal type") || '—',
    },
    {
      title: 'Rate',
      key: 'rate',
      width: 90,
      align: 'right',
      sorter: (a, b) =>
        compareNumeric(
          parseNumeric(getColumnText(a.columns_preview, 'Publishers rate')),
          parseNumeric(getColumnText(b.columns_preview, 'Publishers rate')),
        ),
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sort?.field === 'rate' ? sort.order : null,
      showSorterTooltip: false,
      render: (_value, match) => formatRate(getColumnText(match.columns_preview, 'Publishers rate')),
    },
    {
      title: 'Expected daily',
      key: 'expected',
      width: 130,
      align: 'right',
      sorter: (a, b) =>
        compareNumeric(
          parseNumeric(getColumnText(a.columns_preview, 'Expected daily impressions/clicks')),
          parseNumeric(getColumnText(b.columns_preview, 'Expected daily impressions/clicks')),
        ),
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sort?.field === 'expected' ? sort.order : null,
      showSorterTooltip: false,
      render: (_value, match) =>
        formatExpectedDaily(getColumnText(match.columns_preview, 'Expected daily impressions/clicks')),
    },
    {
      title: 'End date',
      key: 'endDate',
      width: 110,
      sorter: (a, b) => (a.end_date || '').localeCompare(b.end_date || ''),
      sortDirections: SORT_DIRECTIONS,
      sortOrder: sort?.field === 'endDate' ? sort.order : null,
      showSorterTooltip: false,
      render: (_value, match) => match.end_date || '—',
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_value, match) => (
        <Space size={4} className="leadops-match-actions">
          <Popover
            overlayClassName="leadops-match-preview-popover"
            title="Preview"
            content={<MatchPreview match={match} />}
            trigger={['hover', 'click']}
            mouseEnterDelay={0.2}
            placement="bottomRight"
          >
            <Button aria-label="Preview" size="small" icon={<EyeOutlined />} />
          </Popover>
          {match.url && (
            <Tooltip title="Open in Monday">
              <Button
                aria-label="Open"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => window.open(match.url, '_blank', 'noopener,noreferrer')}
              />
            </Tooltip>
          )}
          <Tooltip title="Pitch to advertiser">
            <Button
              aria-label="Pitch"
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={() => onPitch(match)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ], [onPitch]);

  const criticalColumns = useMemo(() => buildColumns(criticalSort), [buildColumns, criticalSort]);
  const readyColumns = useMemo(() => buildColumns(readySort), [buildColumns, readySort]);

  const collapseItems: CollapseProps['items'] = [];
  if (filteredCritical.length > 0) {
    collapseItems.push({
      key: 'critical',
      label: sectionLabel('Critical to sell', filteredCritical.length),
      children: (
        <Table<LeadOpsMatch>
          className="leadops-unsold-table"
          size="small"
          rowKey={(match) => `${match.board_id}-${match.item_id}`}
          columns={criticalColumns}
          dataSource={filteredCritical}
          pagination={false}
          sticky
          scroll={TABLE_SCROLL}
          onChange={(_pagination, _filters, sorter) => handleSortChange(sorter, setCriticalSort)}
        />
      ),
    });
  }
  if (filteredReady.length > 0) {
    collapseItems.push({
      key: 'ready',
      label: sectionLabel('Ready to sell', filteredReady.length),
      children: (
        <Table<LeadOpsMatch>
          className="leadops-unsold-table"
          size="small"
          rowKey={(match) => `${match.board_id}-${match.item_id}`}
          columns={readyColumns}
          dataSource={filteredReady}
          pagination={false}
          sticky
          scroll={TABLE_SCROLL}
          onChange={(_pagination, _filters, sorter) => handleSortChange(sorter, setReadySort)}
        />
      ),
    });
  }

  return (
    <div className="leadops-unsold-panel">
      <div className="leadops-unsold-panel-header">
        <div>
          <Text strong className="leadops-unsold-panel-title">UNSOLD inventory</Text>
          <Text type="secondary" className="leadops-unsold-panel-meta">
            {critical.length} critical · {ready.length} ready
          </Text>
        </div>
        <Input.Search
          allowClear
          className="leadops-unsold-text-filter"
          placeholder="Filter by domain or name…"
          value={textFilter}
          onChange={(event) => setTextFilter(event.target.value)}
        />
      </div>

      {(formatChips.length > 0 || hasActiveFilters) && (
        <div className="leadops-unsold-filter-row">
          {formatChips.length > 0 && (
            <div className="leadops-unsold-format-filters">
              {formatChips.map((label) => {
                const active = selectedNormalized.has(normalizeAdFormat(label));
                return (
                  <Tag
                    key={label}
                    className={`leadops-unsold-format-chip${active ? ' is-active' : ''}`}
                    color={active ? 'green' : undefined}
                    onClick={() => toggleFormat(label)}
                  >
                    {label}
                  </Tag>
                );
              })}
            </div>
          )}
          {hasActiveFilters && (
            <Button
              type="link"
              size="small"
              className="leadops-unsold-clear-filters"
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          )}
        </div>
      )}

      {isLoading && <Skeleton active paragraph={{ rows: 10 }} />}
      {hasError && (
        <Alert type="error" showIcon message="Could not load unsold inventory" />
      )}
      {isEmpty && (
        <Alert type="info" showIcon message="No Ready to sell or Critical to sell spots right now" />
      )}
      {filterEmpty && (
        <Alert type="info" showIcon message="No spots match this filter" />
      )}

      {!isLoading && !hasError && collapseItems.length > 0 && (
        <Collapse
          className="leadops-unsold-collapse"
          activeKey={collapseKeys}
          onChange={(keys) => setCollapseKeys(Array.isArray(keys) ? keys : [keys])}
          items={collapseItems}
        />
      )}
    </div>
  );
};
